import { useState, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
} from '@openzeppelin/ui-components';
import { Icon } from '@ascend/shared/components';
import { SearchBar, PaginationControls, TABLE_PAGE_SIZE_OPTIONS } from '../components/ListControls';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PoolYield {
  id: string;
  name: string;
  deposited: string;
  apy: string;
  accrued: string;
  accruedNum: number;
  totalClaimed: string;
}

// ─── Deterministic Random ─────────────────────────────────────────────────────

function seededRandom(seed: number) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const YIELD_POOL_NAMES = [
  'USDC Lending Pool', 'XSGD Lending Pool', 'EURC Yield Pool', 'GBPT Stable Pool',
  'GYEN Growth Pool', 'CHFR Reserve Pool', 'AUDD Income Pool', 'CADC Prime Pool',
  'NZDS Core Pool', 'HKDC Flex Pool', 'XSGD Ultra Pool', 'MXNT Plus Pool',
  'KRWC Dynamic Pool', 'BRZ Horizon Pool',
];

const POOL_YIELDS: PoolYield[] = YIELD_POOL_NAMES.map((name, i) => {
  const deposited = Math.round(5000 + seededRandom(i * 6 + 1) * 95000);
  const apy = 4 + seededRandom(i * 6 + 2) * 10;
  const accruedNum = parseFloat((deposited * (apy / 100) * seededRandom(i * 6 + 3) * 0.15).toFixed(2));
  const totalClaimed = parseFloat((deposited * (apy / 100) * seededRandom(i * 6 + 4) * 0.4).toFixed(2));
  return {
    id: `pool-${i + 1}`,
    name,
    deposited: `$${deposited.toLocaleString()}`,
    apy: `${apy.toFixed(1)}%`,
    accrued: `$${accruedNum.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    accruedNum,
    totalClaimed: `$${totalClaimed.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
  };
});

function formatCurrency(value: number): string {
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ─── Page Component ───────────────────────────────────────────────────────────

export function LenderYieldPage() {
  const [claimingAll, setClaimingAll] = useState(false);
  const [claimingPool, setClaimingPool] = useState<string | null>(null);

  const [poolSearch, setPoolSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(TABLE_PAGE_SIZE_OPTIONS[0]);

  const totalAccrued = POOL_YIELDS.reduce((sum, p) => sum + p.accruedNum, 0);

  const filteredPools = useMemo(() => {
    if (!poolSearch.trim()) return POOL_YIELDS;
    const q = poolSearch.toLowerCase();
    return POOL_YIELDS.filter((p) => p.name.toLowerCase().includes(q));
  }, [poolSearch]);

  const paginatedPools = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(filteredPools.length / pageSize));
    const safe = Math.min(page, totalPages);
    return filteredPools.slice((safe - 1) * pageSize, safe * pageSize);
  }, [filteredPools, page, pageSize]);

  function handleClaimAll() {
    setClaimingAll(true);
    setTimeout(() => {
      setClaimingAll(false);
      toast.success(`Claimed ${formatCurrency(totalAccrued)} in yield`);
    }, 2000);
  }

  function handleClaimPool(poolId: string, poolName: string, amount: number) {
    setClaimingPool(poolId);
    setTimeout(() => {
      setClaimingPool(null);
      toast.success(`Claimed ${formatCurrency(amount)} from ${poolName}`);
    }, 1500);
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Page header */}
      <div className="flex items-start gap-3">
        <Icon name="streamline:graph-bar-increase" className="mt-1 h-6 w-6 sm:h-7 sm:w-7 text-foreground flex-shrink-0" />
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Yield Overview</h1>
          <p className="text-sm text-muted-foreground">
            Track your lending returns and claim earned yield.
          </p>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
        <StatCard
          title="Total Deposited"
          value="$75,000"
          subtitle="Across all pools"
          icon={<Icon name="streamline:piggy-bank" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Total Earned"
          value="$1,800"
          subtitle="All time"
          icon={<Icon name="streamline:graph-bar-increase" className="h-5 w-5 text-ascend-success" />}
        />
        <StatCard
          title="Unclaimed Yield"
          value={formatCurrency(totalAccrued)}
          subtitle="Ready to claim"
          icon={<Icon name="streamline:coins-stack" className="h-5 w-5 text-ascend-warning" />}
        />
        <StatCard
          title="Average APY"
          value="7.85%"
          subtitle="Weighted across pools"
          icon={<Icon name="streamline:discount-percent-circle" className="h-5 w-5 text-muted-foreground" />}
        />
      </div>

      {/* Claim All */}
      <div className="rounded-xl border border-border p-5 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold">Claim Yield</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Total claimable: <span className="font-semibold text-foreground text-base">{formatCurrency(totalAccrued)}</span>
            </p>
          </div>
          <Button
            disabled={claimingAll || totalAccrued <= 0}
            onClick={handleClaimAll}
          >
            {claimingAll ? (
              <>
                <Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="mr-2 h-4 w-4 animate-spin" />
                Claiming...
              </>
            ) : (
              <>
                <Icon name="streamline:coins-stack" className="mr-2 h-4 w-4" />
                Claim All ({formatCurrency(totalAccrued)})
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Per-pool Breakdown */}
      <Card>
        <CardHeader className="p-6">
          <CardTitle>Per-Pool Breakdown</CardTitle>
          <CardDescription>Yield details for each lending pool.</CardDescription>
        </CardHeader>
        <CardContent className="p-6 pt-0">
          <SearchBar
            value={poolSearch}
            onChange={(v) => { setPoolSearch(v); setPage(1); }}
            placeholder="Search pools..."
            className="mb-4 sm:max-w-sm"
          />

          {/* Desktop table */}
          <div className="hidden sm:block">
            <div className="grid grid-cols-[1fr_100px_70px_100px_110px_90px] gap-3 items-center pb-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              <span>Pool</span>
              <span className="text-right">Deposited</span>
              <span className="text-right">APY</span>
              <span className="text-right">Accrued</span>
              <span className="text-right">Total Claimed</span>
              <span className="text-center">Action</span>
            </div>
            <div className="divide-y divide-border">
              {paginatedPools.length === 0 ? (
                <div className="py-12 text-center text-sm text-muted-foreground">
                  No pools matching &ldquo;{poolSearch}&rdquo;
                </div>
              ) : (
                paginatedPools.map((pool) => {
                  const isClaiming = claimingPool === pool.id;
                  return (
                    <div
                      key={pool.id}
                      className="grid grid-cols-[1fr_100px_70px_100px_110px_90px] gap-3 items-center py-3 text-sm"
                    >
                      <span className="font-medium">{pool.name}</span>
                      <span className="text-right">{pool.deposited}</span>
                      <span className="text-right text-ascend-success">{pool.apy}</span>
                      <span className="text-right font-medium text-ascend-warning">{pool.accrued}</span>
                      <span className="text-right text-muted-foreground">{pool.totalClaimed}</span>
                      <div className="flex justify-center">
                        <button
                          disabled={isClaiming || pool.accruedNum <= 0}
                          onClick={() => handleClaimPool(pool.id, pool.name, pool.accruedNum)}
                          className="rounded-md bg-primary/10 px-3 py-1 text-xs font-medium text-primary hover:bg-primary/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {isClaiming ? 'Claiming...' : 'Claim'}
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Mobile stacked cards */}
          <div className="sm:hidden space-y-3">
            {paginatedPools.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-12 text-center">
                <p className="text-sm text-muted-foreground">No pools matching &ldquo;{poolSearch}&rdquo;</p>
              </div>
            ) : (
              paginatedPools.map((pool) => {
                const isClaiming = claimingPool === pool.id;
                return (
                  <div key={pool.id} className="rounded-lg border border-border p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{pool.name}</span>
                      <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
                        {pool.apy}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-muted-foreground">Deposited</span>
                        <div className="font-semibold">{pool.deposited}</div>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Accrued</span>
                        <div className="font-semibold text-ascend-warning">{pool.accrued}</div>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Total Claimed</span>
                        <div className="font-semibold">{pool.totalClaimed}</div>
                      </div>
                    </div>
                    <button
                      disabled={isClaiming || pool.accruedNum <= 0}
                      onClick={() => handleClaimPool(pool.id, pool.name, pool.accruedNum)}
                      className="w-full rounded-md bg-primary/10 px-3 py-2 text-xs font-medium text-primary hover:bg-primary/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {isClaiming ? 'Claiming...' : `Claim ${pool.accrued}`}
                    </button>
                  </div>
                );
              })
            )}
          </div>

          <PaginationControls
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalItems={filteredPools.length}
            label="pools"
          />
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Helper Components ────────────────────────────────────────────────────────

function StatCard({
  title,
  value,
  subtitle,
  icon,
}: {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <span className="text-xs sm:text-sm text-muted-foreground">{title}</span>
        {icon}
      </div>
      <div className="mt-2 text-2xl sm:text-3xl font-bold">{value}</div>
      {subtitle && <div className="mt-1 text-xs sm:text-sm text-muted-foreground">{subtitle}</div>}
    </div>
  );
}
